import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { keyBy } from 'lodash'

import { fetchWrapper } from '@/reducers/fetch'
import { AnnualTaxFiling } from '@/features/taxes/annual-taxes/annualTaxFilings.slice'
import {
  AnnualTaxFilingForm,
  AnnualTaxFilingFormQuestionnaireStatus,
  TaxfyleJobStatus,
  TaxfyleJobStep,
} from '@/features/taxes/annual-taxes/annualTaxFilingForms.slice'
import { UserEoyReviewProgress } from '@/features/taxes/annual-taxes/tax-checklist/shared/review-steps-and-progresses/userEndOfYearReviewProgress.slice'
import { TaxFormType } from '@/features/taxes/annual-taxes/questionnaires/constants'
import { adminApiAxios } from '@/api'

export interface TaxFilingUser {
  id: number
  uuid: string
  firstName: string
  lastName: string
  email: string
  createdAt: string
  bookkeeperId: number
  bookkeeper: {
    firstName: string
    lastName: string
  }
  ownerId: number
  owner: {
    firstName: string
    lastName: string
  }
  taxPreparerId: number
  taxPreparer: {
    firstName: string
    lastName: string
  }
  taxCoordinatorId: number
  taxCoordinator: {
    firstName: string
    lastName: string
  }
}

export interface AdminAnnualTaxFiling extends Omit<
  AnnualTaxFiling,
  'annualTaxFilingForms'
> {
  annualTaxFilingForms: AnnualTaxFilingForm[]
  annualTaxFilings: TaxFilingUser | null
  user: TaxFilingUser | null
  userEndOfYearReviewProgresses: UserEoyReviewProgress[]
}

export interface AdminAnnualTaxFilingState {
  [key: string]: AdminAnnualTaxFiling
}

const adminAnnualTaxFilingsSlice = createSlice({
  name: 'allAnnualTaxFilings',
  initialState: {} as AdminAnnualTaxFilingState,
  reducers: {
    getAllAnnualTaxFilings: (
      state,
      action: PayloadAction<{ [key: string]: AdminAnnualTaxFiling }>
    ) => ({ ...state, ...action.payload }),
    updateSingleAnnualTaxFiling: (
      state,
      action: PayloadAction<AdminAnnualTaxFiling>
    ) => {
      state[action.payload.id] = action.payload
    },
  },
})

export default adminAnnualTaxFilingsSlice.reducer

// Actions
export const { getAllAnnualTaxFilings, updateSingleAnnualTaxFiling } =
  adminAnnualTaxFilingsSlice.actions

/* This fetches a list of users and their annual tax filings. */

export const FETCH_ADMIN_ANNUAL_TAX_FILINGS_KEY =
  'FETCH_ADMIN_ANNUAL_TAX_FILINGS_KEY'
export const fetchAdminAnnualTaxFilings = ({
  userIds,
  ...rest
}: {
  page: number
  limit: number
  year?: string
  userIds?: number | number[]
  searchNameOrEmail?: string
  taxfyleJobStatus?: TaxfyleJobStatus
  taxfyleJobStep?: TaxfyleJobStep
  taxChecklistStatus?: AnnualTaxFilingFormQuestionnaireStatus
  jobTimeInStatus?: {
    max?: string
    min?: string
  }
  formNeeds?: TaxFormType | 'noServices'
  bookkeeperId?: number
  extensionRequestStatus?: AnnualTaxFilingFormQuestionnaireStatus
  extensionTaxfyleJobStatus?: TaxfyleJobStatus
  exempted?: boolean
}) =>
  fetchWrapper({
    fetchKey: FETCH_ADMIN_ANNUAL_TAX_FILINGS_KEY,
    defaultErrorMessage: 'Error fetching all user tax filings',
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.get<{
        filings: AdminAnnualTaxFiling[]
        count: number
        page: number
      }>('/finances/api/v1/admin/annual_tax_filings', {
        params: {
          ...rest,
          userIds: Array.isArray(userIds)
            ? userIds.join(',')
            : userIds?.toString(),
        },
      })
      dispatch(getAllAnnualTaxFilings(keyBy(json.data.filings, 'id')))
      return json.data
    },
  })

export const getAdminUpdateAnnualTaxFilingFetchKey = (
  annualTaxFilingId: number
) => `ADMIN_UPDATE_ANNUAL_TAX_FILING_KEY_${annualTaxFilingId}`
/* Updates an Annual Tax Filing */
export const updateAdminAnnualTaxFiling = (
  annualTaxFilingId: number,
  data: Partial<AdminAnnualTaxFiling> & {
    personalExtensionFiledExternally?: boolean | null
    businessExtensionFiledExternally?: boolean | null
  }
) =>
  fetchWrapper({
    defaultErrorMessage: 'Error updating annual tax filing',
    fetchKey: getAdminUpdateAnnualTaxFilingFetchKey(annualTaxFilingId),
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.put<AdminAnnualTaxFiling>(
        `/finances/api/v1/admin/annual_tax_filings/${annualTaxFilingId}`,
        data
      )

      dispatch(updateSingleAnnualTaxFiling(json.data))

      return json.data
    },
  })

export const getAdminSubmitTskFetchKey = (annualTaxFilingId: number) =>
  `ADMIN_SUBMIT_TSK_KEY_${annualTaxFilingId}`
/* Submits TSK for an Annual Tax Filing via the dedicated admin endpoint (triggers full side effects) */
export const adminSubmitTsk = (
  annualTaxFilingId: number,
  data: Partial<AdminAnnualTaxFiling> & {
    personalExtensionFiledExternally?: boolean | null
    businessExtensionFiledExternally?: boolean | null
  }
) =>
  fetchWrapper({
    defaultErrorMessage: 'Error submitting TSK for annual tax filing',
    fetchKey: getAdminSubmitTskFetchKey(annualTaxFilingId),
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.put<AdminAnnualTaxFiling>(
        `/finances/api/v1/admin/annual_tax_filings/${annualTaxFilingId}/submit-tsk`,
        data
      )

      dispatch(updateSingleAnnualTaxFiling(json.data))

      return json.data
    },
  })

/* This fetches a list of users and their annual tax filings. We do not save this to the Redux store. */
export const fetchAdminAnnualTaxFilingById = (id: string | undefined) =>
  fetchWrapper({
    defaultErrorMessage: 'Error fetching tax filing',
    fetchFunction: async () => {
      // Note api doesn't return extra admin information here so don't save off
      const json = await adminApiAxios.get<AdminAnnualTaxFiling>(
        `/finances/api/v1/admin/annual_tax_filings/${id}`
      )

      return json.data
    },
  })

// Kicks off a background re-sync of all of the user's eligible documents to
// the filing form's Taxfyle job. Endpoint returns immediately; check Datadog
// for the bulkUploadToJob completion log.
export const RESYNC_TAXFYLE_DOCUMENTS_FETCH_KEY = (filingFormId: number) =>
  `resync-taxfyle-documents-${filingFormId}`

export const resyncTaxfyleDocumentsForFilingForm = (filingFormId: number) =>
  fetchWrapper({
    fetchKey: RESYNC_TAXFYLE_DOCUMENTS_FETCH_KEY(filingFormId),
    defaultErrorMessage: 'Failed to start Taxfyle document re-sync',
    fetchFunction: async () => {
      const response = await adminApiAxios.post<{ ok: true }>(
        `/finances/api/v1/admin/annual_tax_filing_forms/${filingFormId}/resync_taxfyle_documents`
      )
      return response.data
    },
  })
